import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Icon } from '@livable/saas-react-components';
import { track, events } from 'src/configs/amplitude';
import { Typography, Button } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import SplitPageContainer from 'src/components/shared/SplitPageContainer';
import motivationSvg from 'src/img/motivation.svg';
import { useStore } from 'src/stores';
import useStyles from './styles';
import { Link as RouterLink } from 'react-router-dom';
import { StripeForm } from './form';
import { stripeStyles } from './stripeStyles';
export default function PaygateLoaderPage() {
  const classes = useStyles();
  const store = useStore();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const appearance = stripeStyles;
  const navigate = useNavigate();
  const page = 'Paygate';
  const _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    isLoading = _useState2[0],
    setIsLoading = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    clientSecret = _useState4[0],
    setClientSecret = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isAwaitProcess = _useState6[0],
    setIsAwaitProcess = _useState6[1];
  const getPaymentMethods = async () => {
    const resp = await store.payment.getPaymentMethodsAsync.run();
    return resp;
  };
  const getPublicKey = async () => {
    const resp = await store.payment.getPublicKeyAsync.run();
    return resp;
  };
  const getSetupIntentWithSubscription = async () => {
    const resp = await store.payment.getSetupIntentWithSubscriptionAsync.run();
    return resp;
  };
  const handleSetIsAwaitProcess = data => {
    setIsAwaitProcess(data);
  };
  const handleTryDemo = async () => {
    track(events.clicked('Skip Paygate'), {
      page
    });
    track(events.signedUp(), {
      page
    });
    store.account.setIsEnablingDemoMode(true);
    await store.account.enableDemoPropertyAsync.run({
      isEnabled: !store.account.userProfile.isDemoModeEnabled
    });
    await store.account.refreshAccessTokenAsync.run();
    await store.portfolios.getExistingPortfoliosAsync.run();
    await store.company.getCompaniesAsync.run();
    navigate('/active_dashboard');
    window.location.reload();
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Avoid racing GET company-payment-method with POST setup-intent: backend often needs
        // Stripe customer created by setup intent before listing payment methods (first visit 500 → OK after refresh).
        await Promise.all([getPublicKey(), getSetupIntentWithSubscription().then(resp => {
          if (resp && resp.isSuccessful) {
            setClientSecret(resp.response.clientSecret);
          }
        })]);
        if (cancelled) return;
        const paymentMethodsResp = await getPaymentMethods();
        if (cancelled) return;
        const hasLoadedMethods = (paymentMethodsResp === null || paymentMethodsResp === void 0 ? void 0 : paymentMethodsResp.isSuccessful) === true && store.payment.paymentMethods.length > 0;
        if (hasLoadedMethods) {
          store.account.setIsSavingCompany(false);
          store.account.setIsInvite(false);
          await store.company.getCurrentUserCompanyFromTokenAsync.run();
          await store.portfolios.getPortfoliosDashboardAsync.run({
            storeCompany: true
          });
          navigate('/active_dashboard');
        }
      } finally {
        if (!cancelled) {
          store.account.setIsSavingCompany(false);
          setIsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const breadcrumbs = [{
    title: t('sign_up_flow.mfa'),
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "success",
      fontSize: "small",
      state: "primary"
    }),
    link: false
  }, {
    title: t('sign_up_flow.business_information'),
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "success",
      fontSize: "small",
      state: "primary"
    }),
    link: isAwaitProcess ? false : '/company_details'
  }, {
    title: t('sign_up_flow.payment_information'),
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "edit",
      fontSize: "small"
    })
  }];
  const leftBlockContent = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.lgSection
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h2",
    color: "white"
  }, t('sign_up_flow.free_trial_note'))), /*#__PURE__*/React.createElement("div", {
    className: classes.lgSection
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    color: "white"
  }, t('sign_up_flow.our_platform_requires'))), /*#__PURE__*/React.createElement("div", {
    className: classes.smSection
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    color: "white"
  }, t('sign_up_flow.what_will_livable_cost'))), /*#__PURE__*/React.createElement("div", {
    className: classes.xsSection
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    color: "white"
  }, t('sign_up_flow.you_will_be_notified'))));
  if (store.payment.publicKey === '') return null;
  return /*#__PURE__*/React.createElement(SplitPageContainer, {
    imageScr: motivationSvg,
    leftBlock: leftBlockContent,
    breadcrumbs: breadcrumbs,
    showLogOutButton: true
  }, clientSecret && /*#__PURE__*/React.createElement("div", {
    className: classes.stripeWrapper
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "text",
    component: RouterLink,
    size: "small",
    className: classes.backButton,
    to: '/company_details',
    disabled: isAwaitProcess,
    onClick: () => track(events.clicked('Back To Company Details'), {
      page
    }),
    'custom-attribute': '703-paygate-loader-page'
  }, t('sign_up_flow.back_to_business_information')), /*#__PURE__*/React.createElement(Typography, {
    variant: "h3"
  }, t('sign_up_flow.add_payment_information')), /*#__PURE__*/React.createElement("div", {
    className: classes.xlSection
  }, /*#__PURE__*/React.createElement(Elements, {
    options: {
      clientSecret,
      appearance
    },
    stripe: loadStripe(store.payment.publicKey)
  }, /*#__PURE__*/React.createElement(StripeForm, {
    isAwaitProcess: isAwaitProcess || isLoading,
    setIsAwaitProcess: handleSetIsAwaitProcess
  })))), !store.company.companyFeatureStore.disableDemoMode && false && /*#__PURE__*/React.createElement("div", {
    className: classes.tryDemo
  }, /*#__PURE__*/React.createElement(Button, {
    fullWidth: true,
    type: "button",
    variant: "text",
    onClick: handleTryDemo,
    'custom-attribute': '704-paygate-loader-page'
  }, "Skip & Try demo")));
}